import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type InfiniteData,
  type UseQueryOptions,
  skipToken,
} from "@tanstack/react-query";
import { useMemo } from "react";
import z from "zod";

import { extractApplyStreamEvent, parseSSEBuffer } from "./applyStreamEvent";
import type { ApplyProgress } from "./applyStreamEvent";
import { getJWTClient, withJWTClient } from "./connectClient";
import type { SiteId } from "@/types/optins";

import { invalidateFramePreviewQueries } from "@/api/creative-blocks";
import { dragonTilesKeys } from "@/api/dragon-tiles";
import { useOnboardingKit } from "@/features/Onboarding/hooks/useOnboardingKit";
import { DEFAULT_KIT_ID } from "@/features/Onboarding/utils/kits";
import { clearDraft } from "@/features/Onboarding/utils/onboardingDraftStorage";
import {
  assetDataSchema,
  defaultCreativeResponseSchema,
  frameCopyDetailSchema,
  frameVariantsResponseSchema,
  copyCoverageSchema,
  productPreviewsResponseSchema,
  setupHistoryDetailSchema,
  setupHistoryEntryDetailSchema,
  setupHistoryVersionDetailSchema,
  setupKitsDetailSchema,
  setupMetaSchema,
  setupResponseSchema,
  setupSnapshotDeleteDetailSchema,
  type OnboardingStepId,
  type SetupResponse,
  type AssetData,
  type DefaultCreativeRequest,
  type DefaultCreativeResponse,
  type ProductPreviewsResponse,
  type FrameVariantsResponse,
  type CopyCoverage,
  type ApplyToFramesResponse,
  type SetupKitsDetail,
  type SetupKitCreateRequest,
  type FrameCopyDetail,
  type SetupHistoryDetail,
  type SetupHistoryEntryDetail,
  type SetupHistoryVersionDetail,
  type SetupRevertRequest,
  type SetupSnapshotCreateRequest,
  type SetupSnapshotDeleteDetail,
} from "@/types/onboarding";
import { getBackendErrorMessage } from "@/utils/backendError";
import { blockDemoMutation, isDemoUserFromStorage } from "@/utils/demoMode";

export const SETUP_API_BASE_URL = import.meta.env
  .VITE_ONBOARDING_API_URL as string;

// Query-string for kit-scoped GET/POST routes. Empty for the default kit so
// default traffic stays byte-identical to the pre-kit backend contract.
// Exported for tests.
export function kitQueryString(kit?: string): string {
  return kit && kit !== DEFAULT_KIT_ID ? `?kit=${encodeURIComponent(kit)}` : "";
}

type PersistedOnboardingStepId = Exclude<
  OnboardingStepId,
  "scope" | "complete" | "results"
>;

const setupUpdateResponseSchema = z
  .object({
    components: z.unknown().optional(),
    intro: z.unknown().optional(),
    meta: setupMetaSchema.optional(),
    operation: z.unknown().optional(),
    styling: z.unknown().optional(),
    voice: z.unknown().optional(),
  })
  .passthrough();

type ParsedSetupUpdateResponse = z.infer<typeof setupUpdateResponseSchema>;
type UpdateSetupDataResponse = {
  raw: unknown;
  parsed: ParsedSetupUpdateResponse | null;
};

const STEP_META_KEY_MAP: Record<
  PersistedOnboardingStepId,
  keyof SetupResponse["meta"]
> = {
  intro: "introUpdatedAt",
  styling: "stylingUpdatedAt",
  operation: "operationUpdatedAt",
  components: "componentsUpdatedAt",
  voice: "voiceUpdatedAt",
};

function parseSetupUpdateResponse(data: unknown): UpdateSetupDataResponse {
  const parsed = setupUpdateResponseSchema.safeParse(data);

  return {
    raw: data,
    parsed: parsed.success ? parsed.data : null,
  };
}

export async function getSetupData(
  siteId: SiteId,
  kit?: string,
): Promise<SetupResponse> {
  const response = await withJWTClient((client) =>
    client.get(`${SETUP_API_BASE_URL}/${siteId}/setup${kitQueryString(kit)}`),
  );
  return setupResponseSchema.parse(response.data);
}

async function getAllStepsTempDataReal(
  siteId: SiteId,
  domain: string,
): Promise<SetupResponse> {
  try {
    if (!siteId) {
      throw new Error("Site ID is required");
    }
    if (!domain) {
      throw new Error("Domain is required");
    }

    const response = await withJWTClient((client) =>
      client.get(
        `${SETUP_API_BASE_URL}/${siteId}/setup/default?domain=${encodeURIComponent(domain)}`,
      ),
    );

    if (!response?.data) {
      throw new Error("API returned empty response");
    }

    const raw: unknown = response.data;

    // Validate response structure
    if (typeof raw !== "object" || raw === null) {
      throw new Error("Invalid response format: expected object");
    }

    // The schema tolerates absent sections; /setup/default omitting them
    // means the bootstrap failed — keep the loud errors.
    for (const key of ["styling", "operation", "voice"] as const) {
      if (!(key in raw)) {
        throw new Error(`Response missing required field: ${key}`);
      }
    }

    return setupResponseSchema.parse(raw);
  } catch (error) {
    throw new Error(
      `Failed to fetch temporary setup data: ${getBackendErrorMessage(error)}`,
    );
  }
}

// A domain Prisma has never seen is ingested on demand (≈3–6 min cold).
// Meanwhile /setup/default answers from Brandfetch/logo.dev with
// brandSource "extracting", so re-ask until Prisma's record leads, then use
// that answer. If the deadline passes, the signal aborts or a re-ask fails,
// keep the last good answer: that is the pre-Prisma outcome, not an error.
// Each "extracting" answer goes to onPartial: once Prisma has the DNA stage
// (~20-45 s) it already carries the brand's copy + colours, worth showing.
export const PRISMA_INGEST_POLL_MS = 5_000;
export const PRISMA_INGEST_DEADLINE_MS = 7 * 60_000;

export const getAllStepsTempData = async (
  siteId: SiteId,
  domain: string,
  signal?: AbortSignal,
  onPartial?: (data: SetupResponse) => void,
): Promise<SetupResponse> => {
  signal?.throwIfAborted();
  const deadline = Date.now() + PRISMA_INGEST_DEADLINE_MS;
  let data = await getAllStepsTempDataReal(siteId, domain);
  while (
    data.brandSource === "extracting" &&
    Date.now() < deadline &&
    !signal?.aborted
  ) {
    onPartial?.(data);
    await new Promise((resolve) => setTimeout(resolve, PRISMA_INGEST_POLL_MS));
    try {
      data = await getAllStepsTempDataReal(siteId, domain);
    } catch {
      break;
    }
  }
  return data;
};

export async function updateSetupData(
  siteId: SiteId,
  step: OnboardingStepId,
  data: unknown,
  kit?: string,
): Promise<UpdateSetupDataResponse> {
  const response = await withJWTClient((client) =>
    client.post(
      `${SETUP_API_BASE_URL}/${siteId}/setup/${step}${kitQueryString(kit)}`,
      data,
    ),
  );

  return parseSetupUpdateResponse(response.data);
}

async function uploadAssetReal(siteId: SiteId, file: File): Promise<AssetData> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await withJWTClient((client) =>
    client.post(`${SETUP_API_BASE_URL}/${siteId}/setup/asset`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  );

  return assetDataSchema.parse(response.data);
}

export const uploadAsset = (siteId: SiteId, file: File): Promise<AssetData> => {
  return uploadAssetReal(siteId, file);
};

async function generateDefaultCreativeReal(
  siteId: SiteId,
  request: DefaultCreativeRequest,
): Promise<DefaultCreativeResponse> {
  const response = await withJWTClient((client) =>
    client.post(
      `${SETUP_API_BASE_URL}/${siteId}/setup/default_creative`,
      request,
    ),
  );
  return defaultCreativeResponseSchema.parse(response.data);
}

export const generateDefaultCreative = (
  siteId: SiteId,
  request: DefaultCreativeRequest,
): Promise<DefaultCreativeResponse> => {
  return generateDefaultCreativeReal(siteId, request);
};

const getSampleProducts = async (
  siteId: SiteId,
  kit?: string,
): Promise<ProductPreviewsResponse> => {
  return await withJWTClient(async (client) => {
    const response = await client.get(
      `${SETUP_API_BASE_URL}/${siteId}/setup/sample_products${kitQueryString(kit)}`,
    );
    return productPreviewsResponseSchema.parse(response.data);
  });
};

export const getFrameVariants = async (): Promise<FrameVariantsResponse> => {
  return await withJWTClient(async (client) => {
    const response = await client.get(`${SETUP_API_BASE_URL}/creative/frames`);
    return frameVariantsResponseSchema.parse(response.data);
  });
};

// `frameId` narrows the check to one blueprint (the save path), which spares
// the backend from hydrating every templates-site frame.
export const getCopyCoverage = async (
  frameId?: string,
): Promise<CopyCoverage> => {
  return await withJWTClient(async (client) => {
    const response = await client.get(
      `${SETUP_API_BASE_URL}/creative/copy_coverage`,
      { params: frameId ? { frame_id: frameId } : undefined },
    );
    return copyCoverageSchema.parse(response.data);
  });
};

// --- Brand kit CRUD (branddetails-v2 PR #40). All three return the full
// updated registry, so mutations write the response straight into the cache.

export async function getKits(siteId: SiteId): Promise<SetupKitsDetail> {
  const response = await withJWTClient((client) =>
    client.get(`${SETUP_API_BASE_URL}/${siteId}/setup/kits`),
  );
  return setupKitsDetailSchema.parse(response.data);
}

export async function createKit(
  siteId: SiteId,
  request: SetupKitCreateRequest,
): Promise<SetupKitsDetail> {
  const response = await withJWTClient((client) =>
    client.post(`${SETUP_API_BASE_URL}/${siteId}/setup/kits`, request),
  );
  return setupKitsDetailSchema.parse(response.data);
}

export async function deleteKit(
  siteId: SiteId,
  kitId: string,
): Promise<SetupKitsDetail> {
  const response = await withJWTClient((client) =>
    client.delete(
      `${SETUP_API_BASE_URL}/${siteId}/setup/kits/${encodeURIComponent(kitId)}`,
    ),
  );
  return setupKitsDetailSchema.parse(response.data);
}

// Latest per-tile generated copy. Site-level (no ?kit= — the payload carries
// which kit produced it); 404s before the first copy-generating apply.
export async function getFrameCopy(siteId: SiteId): Promise<FrameCopyDetail> {
  const response = await withJWTClient((client) =>
    client.get(`${SETUP_API_BASE_URL}/${siteId}/setup/frame_copy`),
  );
  return frameCopyDetailSchema.parse(response.data);
}

// --- Brand-book history & revert. as_of and revert are kit-scoped (?kit=,
// default when omitted); apply rows are site-level, so every kit's feed shows
// every apply, kit-labelled.

export async function getSetupHistory(
  siteId: SiteId,
  params: { section?: string; limit?: number; before?: string } = {},
): Promise<SetupHistoryDetail> {
  const query = new URLSearchParams();
  if (params.section) query.set("section", params.section);
  if (params.limit) query.set("limit", String(params.limit));
  if (params.before) query.set("before", params.before);
  const qs = query.toString();
  const response = await withJWTClient((client) =>
    client.get(
      `${SETUP_API_BASE_URL}/${siteId}/setup/history${qs ? `?${qs}` : ""}`,
    ),
  );
  return setupHistoryDetailSchema.parse(response.data);
}

// One stored version. Section "frame_copy" returns the raw per-tile copy
// artifact of an apply run: its versionId is the apply row's versionId with
// the "marker" segment swapped for "copy" (see pairedFrameCopyVersionId).
// 404 for applies that predate copy persistence or generated no copy.
export async function getHistoryVersion(
  siteId: SiteId,
  section: string,
  versionId: string,
): Promise<SetupHistoryVersionDetail> {
  const response = await withJWTClient((client) =>
    client.get(
      `${SETUP_API_BASE_URL}/${siteId}/setup/history/${encodeURIComponent(section)}/${encodeURIComponent(versionId)}`,
    ),
  );
  return setupHistoryVersionDetailSchema.parse(response.data);
}

// The brand book as it stood at an instant, for one kit. Sections that did not
// exist yet are null.
export async function getSetupAsOf(
  siteId: SiteId,
  at: string,
  kit: string = DEFAULT_KIT_ID,
): Promise<SetupResponse> {
  const kitParam =
    kit !== DEFAULT_KIT_ID ? `&kit=${encodeURIComponent(kit)}` : "";
  const response = await withJWTClient((client) =>
    client.get(
      `${SETUP_API_BASE_URL}/${siteId}/setup/as_of?at=${encodeURIComponent(at)}${kitParam}`,
    ),
  );
  return setupResponseSchema.parse(response.data);
}

// Revert appends new versions restoring older state to one kit's stack —
// history is never rewritten, so a revert is itself an auditable history row.
export async function revertSetup(
  siteId: SiteId,
  request: SetupRevertRequest,
  kit: string = DEFAULT_KIT_ID,
): Promise<SetupResponse> {
  const kitQs = kit !== DEFAULT_KIT_ID ? `?kit=${encodeURIComponent(kit)}` : "";
  const response = await withJWTClient((client) =>
    client.post(
      `${SETUP_API_BASE_URL}/${siteId}/setup/revert${kitQs}`,
      request,
    ),
  );
  return setupResponseSchema.parse(response.data);
}

// Creates a "save progress" snapshot for one kit: a site-level label object
// pointing at this instant — a pointer, not a copy; Preview/Restore reuse
// as_of/revert. Returns the new history feed entry (kind "snapshot") so the
// drawer can insert the row without a refetch. The body is always sent (the
// backend 400s an empty body; an empty label stores as null).
export async function createSnapshot(
  siteId: SiteId,
  request: SetupSnapshotCreateRequest,
  kit: string = DEFAULT_KIT_ID,
): Promise<SetupHistoryEntryDetail> {
  const response = await withJWTClient((client) =>
    client.post(
      `${SETUP_API_BASE_URL}/${siteId}/setup/snapshot${kitQueryString(kit)}`,
      request,
    ),
  );
  return setupHistoryEntryDetailSchema.parse(response.data);
}

// Deletes ONE snapshot label object — never brand-book version data, so
// deleting a save point never loses work. Snapshots are site-level rows
// identified by id: no kit selector.
export async function deleteSnapshot(
  siteId: SiteId,
  versionId: string,
): Promise<SetupSnapshotDeleteDetail> {
  const response = await withJWTClient((client) =>
    client.delete(
      `${SETUP_API_BASE_URL}/${siteId}/setup/snapshot/${encodeURIComponent(versionId)}`,
    ),
  );
  return setupSnapshotDeleteDetailSchema.parse(response.data);
}

// An apply row's paired frame_copy artifact id: same {ts}[_{key}] name, only
// the fixed "marker"/"copy" segment differs (both objects are written at the
// same instant with the same random key). Exported for tests.
export function pairedFrameCopyVersionId(applyVersionId: string): string {
  return applyVersionId.replace(/_marker(?=_|$)/, "_copy");
}

// Kit-scoped step keys all share one shape; the default keeps kit-less
// callers (and pre-kit cache entries) on the default kit's key.
const stepKey =
  (step: string) =>
  (siteId: SiteId, kit: string = DEFAULT_KIT_ID) => [
    "onboarding",
    step,
    siteId,
    kit,
  ];

export const onboardingKeys = {
  allSteps: stepKey("allSteps"),
  allStepsTemp: (siteId: SiteId, domain: string) => [
    "onboarding",
    "allStepsTemp",
    siteId,
    domain,
  ],
  intro: stepKey("intro"),
  styling: stepKey("styling"),
  operation: stepKey("operation"),
  components: stepKey("components"),
  voice: stepKey("voice"),
  kits: (siteId: SiteId) => ["onboarding", "kits", siteId],
  frameCopy: (siteId: SiteId) => ["onboarding", "frameCopy", siteId],
  // Without a section this is the invalidation PREFIX for every history
  // query of the site; with one it is the exact key for that stream.
  history: (siteId: SiteId, section?: string) =>
    section
      ? ["onboarding", "history", siteId, section]
      : ["onboarding", "history", siteId],
  asOf: (siteId: SiteId, at: string, kit: string = DEFAULT_KIT_ID) => [
    "onboarding",
    "asOf",
    siteId,
    at,
    kit,
  ],
  historyVersion: (siteId: SiteId, section: string, versionId: string) => [
    "onboarding",
    "historyVersion",
    siteId,
    section,
    versionId,
  ],
  defaultCreative: (siteId: SiteId, requestHash: string) => [
    "onboarding",
    "defaultCreative",
    siteId,
    requestHash,
  ],
  sampleProducts: (
    siteId: SiteId,
    domain: string,
    kit: string = DEFAULT_KIT_ID,
  ) => ["onboarding", "sampleProducts", siteId, kit, domain],
  frameVariants: () => ["onboarding", "frameVariants"],
  copyCoverage: () => ["onboarding", "copyCoverage"],
  filteredFrameVariants: () => ["onboarding", "filteredFrameVariants"],
};

const STEP_QUERY_KEY_MAP: Record<
  OnboardingStepId,
  (siteId: SiteId, kit?: string) => readonly unknown[]
> = {
  intro: onboardingKeys.intro,
  styling: onboardingKeys.styling,
  operation: onboardingKeys.operation,
  components: onboardingKeys.components,
  voice: onboardingKeys.voice,
  // Flow-control / terminal UI steps with no data of their own — never saved,
  // but the map is keyed by the full OnboardingStepId union, so they need an
  // entry.
  scope: onboardingKeys.allSteps,
  complete: onboardingKeys.allSteps,
  results: onboardingKeys.allSteps,
};

function mergeUpdatedSetupStep(
  oldData: SetupResponse,
  step: OnboardingStepId,
  response: UpdateSetupDataResponse,
): SetupResponse {
  const responseMeta = response.parsed?.meta;
  const mergedMeta = {
    ...oldData.meta,
    ...(responseMeta ?? {}),
    ...(step !== "scope" &&
    step !== "complete" &&
    step !== "results" &&
    responseMeta === undefined
      ? {
          [STEP_META_KEY_MAP[step]]:
            oldData.meta?.[STEP_META_KEY_MAP[step]] ?? new Date().toUTCString(),
        }
      : {}),
  };

  // Flow-control / terminal UI steps carry no step data of their own — only
  // the meta merge.
  if (step === "scope" || step === "complete" || step === "results") {
    return {
      ...oldData,
      meta: mergedMeta,
    };
  }

  const parsedStepData = response.parsed?.[step];
  const nextStepData =
    parsedStepData !== undefined ? parsedStepData : response.raw;

  return {
    ...oldData,
    meta: mergedMeta,
    [step]: nextStepData,
  };
}

export const allStepsDataQueryOptions = (
  siteId: SiteId,
  kit: string = DEFAULT_KIT_ID,
) => ({
  queryKey: onboardingKeys.allSteps(siteId, kit),
  queryFn: () => getSetupData(siteId, kit),
});

// Shared by useProductPreviews and the brand-onboarding route's warm-up
// prefetch, so both hit the same cache entry.
//
// The kit is a real request parameter: the backend resolves the brand from
// that kit's stored intro section, so each treatment's own website is what
// gets its catalog read. Domain stays in the key so a re-map within one kit
// invalidates, and it gates fetching to sites that have a domain at all.
export const sampleProductsQueryOptions = (
  siteId: SiteId,
  domain: string,
  kit?: string,
) => ({
  queryKey: onboardingKeys.sampleProducts(siteId, domain, kit),
  queryFn: () => getSampleProducts(siteId, kit),
});

// Individual step hooks that return only their specific data
export function useIntroData(
  siteId: SiteId,
  options?: Omit<
    UseQueryOptions<SetupResponse["intro"], Error>,
    "queryKey" | "queryFn"
  >,
) {
  const kit = useOnboardingKit();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: onboardingKeys.intro(siteId, kit),
    queryFn: async () => {
      const data = await getSetupData(siteId, kit);
      return data.intro;
    },
    enabled: !!siteId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: () => {
      // Use intro data from allSteps query if available
      const allStepsData = queryClient.getQueryData(
        onboardingKeys.allSteps(siteId, kit),
      ) as SetupResponse;
      return allStepsData?.intro;
    },
    ...options,
  });
}

export function useStylingData(
  siteId: SiteId,
  options?: Omit<
    UseQueryOptions<SetupResponse["styling"], Error>,
    "queryKey" | "queryFn"
  >,
) {
  const kit = useOnboardingKit();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: onboardingKeys.styling(siteId, kit),
    queryFn: async () => {
      const data = await getSetupData(siteId, kit);
      return data.styling;
    },
    enabled: !!siteId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: () => {
      // Use styling data from allSteps query if available
      const allStepsData = queryClient.getQueryData(
        onboardingKeys.allSteps(siteId, kit),
      ) as SetupResponse;
      return allStepsData?.styling;
    },
    ...options,
  });
}

export function useOperationData(
  siteId: SiteId,
  options?: Omit<
    UseQueryOptions<SetupResponse["operation"], Error>,
    "queryKey" | "queryFn"
  >,
) {
  const kit = useOnboardingKit();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: onboardingKeys.operation(siteId, kit),
    queryFn: async () => {
      const data = await getSetupData(siteId, kit);
      return data.operation;
    },
    enabled: !!siteId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: () => {
      // Use operation data from allSteps query if available
      const allStepsData = queryClient.getQueryData(
        onboardingKeys.allSteps(siteId, kit),
      ) as SetupResponse;
      return allStepsData?.operation;
    },
    ...options,
  });
}

export function useComponentsData(
  siteId: SiteId,
  options?: Omit<
    UseQueryOptions<SetupResponse["components"], Error>,
    "queryKey" | "queryFn"
  >,
) {
  const kit = useOnboardingKit();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: onboardingKeys.components(siteId, kit),
    queryFn: async () => {
      const data = await getSetupData(siteId, kit);
      return data.components;
    },
    enabled: !!siteId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: () => {
      // Use components data from allSteps query if available
      const allStepsData = queryClient.getQueryData(
        onboardingKeys.allSteps(siteId, kit),
      ) as SetupResponse;
      return allStepsData?.components;
    },
    ...options,
  });
}

export function useVoiceData(
  siteId: SiteId,
  options?: Omit<
    UseQueryOptions<SetupResponse["voice"], Error>,
    "queryKey" | "queryFn"
  >,
) {
  const kit = useOnboardingKit();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: onboardingKeys.voice(siteId, kit),
    queryFn: async () => {
      const data = await getSetupData(siteId, kit);
      return data.voice;
    },
    enabled: !!siteId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: () => {
      // Use voice data from allSteps query if available
      const allStepsData = queryClient.getQueryData(
        onboardingKeys.allSteps(siteId, kit),
      ) as SetupResponse;
      return allStepsData?.voice;
    },
    ...options,
  });
}

export function useUpdateSetupData() {
  const kit = useOnboardingKit();
  const queryClient = useQueryClient();

  return useMutation<
    UpdateSetupDataResponse,
    Error,
    {
      siteId: SiteId;
      step: OnboardingStepId;
      data: unknown;
    }
  >({
    mutationFn: ({ siteId, step, data }) =>
      updateSetupData(siteId, step, data, kit),
    onSuccess: (data, { siteId, step }) => {
      // Update the allSteps cache with the new data to avoid refetching
      queryClient.setQueryData(
        onboardingKeys.allSteps(siteId, kit),
        (oldData: SetupResponse | undefined) => {
          if (!oldData) return oldData;
          return mergeUpdatedSetupStep(oldData, step, data);
        },
      );

      // Invalidate the specific step cache to ensure it's fresh
      void queryClient.invalidateQueries({
        queryKey: STEP_QUERY_KEY_MAP[step](siteId, kit),
      });
    },
  });
}

export function useUploadAsset() {
  const queryClient = useQueryClient();

  return useMutation<
    AssetData,
    Error,
    {
      siteId: SiteId;
      file: File;
    }
  >({
    mutationFn: ({ siteId, file }) => uploadAsset(siteId, file),
    onSuccess: (_, { siteId }) => {
      // Only invalidate the allSteps cache since assets are referenced there
      // Individual step caches will get updated data via initialData from allSteps
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.allSteps(siteId),
      });
    },
  });
}

export function useGenerateDefaultCreative(siteId?: SiteId) {
  return useMutation<
    DefaultCreativeResponse,
    Error,
    {
      siteId: SiteId;
      request: DefaultCreativeRequest;
    }
  >({
    mutationKey: ["generateDefaultCreative", siteId],
    mutationFn: ({ siteId, request }) =>
      generateDefaultCreative(siteId, request),
    // Prevent garbage collection during onboarding session
    // Keep mutation state available across navigation and page refreshes
    gcTime: 1000 * 60 * 60 * 6, // 6 hours
  });
}

// Variables for the apply-to-frames mutation. Mirrors the
// `GET /<siteId>/setup/apply_to_frames_stream` query contract: omit `frameIds` to
// apply all frames, or pass specific ids (repeated `frame_id`) to apply only those
// — used by the results step to retry just the frames that failed.
export type ApplyToFramesVariables = {
  siteId: SiteId;
  kit?: string; // `kit` query param; omitted for the default kit
  frameIds?: string[]; // repeated `frame_id`; omitted => all frames
  ignoreCopy?: boolean; // `ignore_copy`   (default false)
  forceCopy?: boolean; // `force_copy`     (default false)
  // `force_subject_line_preview_text` (default false): generated copy replaces
  // existing subject lines / preview text instead of only filling blanks.
  forceSubjectLinePreviewText?: boolean;
  defaultSlug?: string; // `default_slug`
  includeDebug?: boolean; // `include_debug` (default false)
  // Restore flow: apply this stored frame_copy artifact's per-tile copy
  // verbatim instead of generating new copy (`copy_artifact` query param).
  copyArtifact?: string;
  // Restore flow: widen a copy_artifact apply from the artifact's affected
  // tiles (the backend default when frameIds is omitted) to every tile on the
  // site (`scope=all` query param). Tiles without recorded copy are re-styled
  // only. Never combined with frameIds — the backend rejects the pair.
  scope?: "all";
  // Live progress (overall pct + render batch count) reported as SSE frames
  // arrive. Supplied internally by useApplyToFrames (which publishes it to the
  // query cache — see applyToFramesProgressKey) rather than by callers; purely
  // presentational, not persisted with the response.
  onProgress?: (progress: ApplyProgress) => void;
};

// Query string for the streaming apply endpoint. Exported for tests.
export function buildApplyToFramesQuery({
  frameIds,
  ignoreCopy,
  forceCopy,
  forceSubjectLinePreviewText,
  defaultSlug,
  includeDebug,
  kit,
  copyArtifact,
  scope,
}: ApplyToFramesVariables): string {
  const params = new URLSearchParams();
  frameIds?.forEach((id) => params.append("frame_id", id));
  if (ignoreCopy) params.set("ignore_copy", "true");
  if (forceCopy) params.set("force_copy", "true");
  if (forceSubjectLinePreviewText)
    params.set("force_subject_line_preview_text", "true");
  if (defaultSlug) params.set("default_slug", defaultSlug);
  if (includeDebug) params.set("include_debug", "true");
  if (kit && kit !== DEFAULT_KIT_ID) params.set("kit", kit);
  if (copyArtifact) params.set("copy_artifact", copyArtifact);
  if (scope) params.set("scope", scope);

  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

// Streaming apply: the `_stream` endpoint performs the apply itself and emits
// `data:` SSE frames (progress updates, then the final ApplyToFramesResponse). We
// read it with fetch + a ReadableStream reader because the axios-based JWT client
// buffers the whole body and can't surface progress mid-run.
// NOTE: fetch bypasses the JWT client's axios auto-refresh interceptor, so we send
// the current bearer token as-is — acceptable for a short-lived apply run.
export async function applyToFrames(
  variables: ApplyToFramesVariables,
): Promise<ApplyToFramesResponse> {
  const { siteId, onProgress } = variables;
  // GET by transport but a mutation in effect — enforce the demo block here
  // since this fetch bypasses the guarded JWT client.
  if (isDemoUserFromStorage()) return blockDemoMutation();
  const token = getJWTClient()?.currentJWT?.token;
  if (!token) throw new Error("JWT client not available");

  // eslint-disable-next-line demo-guard/no-unguarded-requests -- SSE stream; demo-blocked above
  const response = await fetch(
    `${SETUP_API_BASE_URL}/${siteId}/setup/apply_to_frames_stream${buildApplyToFramesQuery(variables)}`,
    {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok || !response.body) {
    throw new Error(
      `Failed to apply to frames: ${response.status} ${response.statusText}`,
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result: ApplyToFramesResponse | undefined;
  // The stepper (applyPhases.ts) derives step done/pending straight off the
  // latest progress, trusting pct to be monotonic. The backend's two
  // interleaving branches aren't contracted to be, so clamp regressions here at
  // the single choke point — a backwards pct would demote a done step to pending.
  let maxProgress = -1;
  // Only render-batch frames carry a count; carry the latest forward so the
  // stepper keeps reading "4 out of 4 batches" through the save/done frames.
  let count: Pick<ApplyProgress, "current" | "total"> = {};

  const handleEvent = (raw: string) => {
    if (!raw || raw === "[DONE]") return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return; // ignore keep-alives / non-JSON comments
    }
    const event = extractApplyStreamEvent(parsed);
    if (event.error) throw new Error(event.error);
    if (event.progress !== undefined && event.progress > maxProgress) {
      maxProgress = event.progress;
      if (event.total !== undefined) {
        count = { current: event.current, total: event.total };
      }
      onProgress?.({ pct: event.progress, ...count });
    }
    if (event.result) result = event.result;
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (value) {
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSSEBuffer(buffer);
        buffer = rest;
        for (const raw of events) handleEvent(raw);
      }
      if (done) break;
    }
    // Flush a trailing frame the stream may have closed without a final blank line.
    buffer += decoder.decode();
    for (const raw of parseSSEBuffer(`${buffer}\n\n`).events) handleEvent(raw);
  } catch (error) {
    void reader.cancel().catch(() => {});
    // The result frame is the complete response, so once it has arrived the run
    // succeeded server-side. A late unclean close (proxy RST, missing terminal
    // chunk) must not fail the run and make Retry re-apply everything — recover
    // with the parsed result. Only rethrow when we have nothing to return.
    if (!result) throw error;
  }

  if (!result) {
    throw new Error("Apply to frames stream ended without a result");
  }
  return result;
}

// Live apply progress (pct + render batch count) for the current run, published to the query cache
// so it survives remounts and is shared by every mounted step — the same reason
// status/data/error are read via useMutationState. A fresh mount that starts
// observing an already-running apply (StepComplete's bounce-back path, or
// StepResults rendering the pending modal for an apply StepComplete started) reads
// the live value here instead of a stale callback bound to the instance that
// called mutate(). Written as SSE frames arrive; reset to the `null` "cleared"
// sentinel when the run settles.
const applyToFramesProgressKey = (siteId: SiteId, kit: string) =>
  ["applyToFramesProgress", siteId, kit] as const;

// Read the live apply progress published by useApplyToFrames. Returns undefined
// before the first progress frame and once the run settles (drives the
// indeterminate loading bar). The value is written imperatively via setQueryData;
// skipToken is the sanctioned "never fetch" queryFn (a missing queryFn logs a
// dev console error on every mount since react-query 5.90, even when disabled).
// kitOverride: same contract as useApplyToFrames — the history drawer reads the
// restored ROW's kit's progress; wizard call sites omit it.
export function useApplyToFramesProgress(
  siteId: SiteId,
  kitOverride?: string,
): ApplyProgress | undefined {
  const urlKit = useOnboardingKit();
  const kit = kitOverride ?? urlKit;
  const { data } = useQuery<ApplyProgress | null>({
    queryKey: applyToFramesProgressKey(siteId, kit),
    queryFn: skipToken,
  });
  // `null` is the "settled / cleared" sentinel — setQueryData ignores an
  // `undefined` value, so a run can't clear with undefined. Map it back so callers
  // see a single "no progress" value.
  return data ?? undefined;
}

// kitOverride targets a kit other than the wizard URL's (the history drawer's
// restore re-applies under the restored ROW's kit); wizard call sites omit it.
export function useApplyToFrames(siteId: SiteId, kitOverride?: string) {
  const urlKit = useOnboardingKit();
  const kit = kitOverride ?? urlKit;
  const queryClient = useQueryClient();

  return useMutation<ApplyToFramesResponse, Error, ApplyToFramesVariables>({
    // Kit-scoped so a switch to another kit can't read this kit's pending /
    // settled apply (see the kit-scoped useMutationState filters + progress key).
    // The drawer's restore-apply (the only kitOverride caller) files under its
    // own ROOT segment, not a ["applyToFrames", siteId, kit, "restore"] suffix:
    // mutation filters partial-match by default, so a suffixed key would still
    // be picked up by the wizard's ["applyToFrames", siteId, kit] filters
    // (StepComplete/StepResults' useMutationState, removeSettledApplyMutations).
    mutationKey: kitOverride
      ? ["applyToFramesRestore", siteId, kit]
      : ["applyToFrames", siteId, kit],
    mutationFn: (variables) =>
      applyToFrames({
        ...variables,
        kit,
        // Bridge stream progress into the query cache so every mounted step reads
        // the same live value (see useApplyToFramesProgress); overrides any
        // caller-supplied onProgress.
        onProgress: (progress) =>
          queryClient.setQueryData(
            applyToFramesProgressKey(variables.siteId, kit),
            progress,
          ),
      }),
    onSettled: (_data, _error, { siteId }) => {
      // Clear the shared progress once the run finishes so a settled value never
      // lingers into the next run or for a late observer.
      queryClient.setQueryData(applyToFramesProgressKey(siteId, kit), null);
    },
    onSuccess: (_data, { siteId }) => {
      void queryClient.invalidateQueries({
        queryKey: dragonTilesKeys.all(siteId),
      });
      // The apply records the copy-applied marker that feeds appliedKit —
      // refresh the registry so the switcher's "applied" tag updates.
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.kits(siteId),
      });
      // The apply also persists a fresh per-tile copy artifact.
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.frameCopy(siteId),
      });
      // Every apply appends a history row (and can move the Applied chip) —
      // keep the history drawer's feed fresh.
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.history(siteId),
        exact: false,
      });
      invalidateFramePreviewQueries(queryClient, siteId);
    },
  });
}

export function useProductPreviews(
  siteId: SiteId,
  domain: string,
  options?: Omit<
    UseQueryOptions<ProductPreviewsResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  const kit = useOnboardingKit();
  return useQuery({
    ...sampleProductsQueryOptions(siteId, domain, kit),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!siteId && !!domain,
    ...options,
  });
}

export function useFrameVariants(
  options?: Omit<
    UseQueryOptions<FrameVariantsResponse, Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: onboardingKeys.frameVariants(),
    queryFn: () => getFrameVariants(),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  });
}

// Blueprints whose bound copy slots D20 won't fill. Self-hiding like useKits:
// `retry: false`, and consumers show nothing without data.
export function useCopyCoverage() {
  return useQuery<CopyCoverage, Error>({
    queryKey: onboardingKeys.copyCoverage(),
    // Wrapped so React Query's context object isn't taken for a frameId.
    queryFn: () => getCopyCoverage(),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export function useFilteredFrameVariants(): FrameVariantsResponse {
  const queryClient = useQueryClient();
  return useMemo(
    () =>
      queryClient.getQueryData(onboardingKeys.filteredFrameVariants()) || [],
    [queryClient],
  );
}

// Kit registry. `retry: false` + self-hiding consumers give graceful
// degradation: against a backend without PR #40 this 404s once and the kit UI
// simply never renders.
//
// Shared by useKits and the brand-onboarding route's entry-kit lookup so both
// describe the same cache entry — the route used to re-declare the key inline
// and the entry's freshness depended on which caller populated it first.
export const kitsQueryOptions = (siteId: SiteId) => ({
  queryKey: onboardingKeys.kits(siteId),
  queryFn: () => getKits(siteId),
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 30_000,
});

export function useKits(siteId: SiteId) {
  return useQuery<SetupKitsDetail, Error>({
    ...kitsQueryOptions(siteId),
    enabled: !!siteId,
  });
}

// Latest per-tile generated copy. Same self-hiding contract as useKits:
// `retry: false` and consumers render nothing without data, so a 404 (no
// copy-generating apply yet, or a backend without the route) costs one
// request and no UI.
// `enabled` lets a caller that only needs the copy inside a rarely-opened
// surface stay dark until then (the history drawer's results table).
export function useFrameCopy(siteId: SiteId, enabled = true) {
  return useQuery<FrameCopyDetail, Error>({
    queryKey: onboardingKeys.frameCopy(siteId),
    queryFn: () => getFrameCopy(siteId),
    enabled: !!siteId && enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

export function useCreateKit(siteId: SiteId) {
  const queryClient = useQueryClient();
  return useMutation<SetupKitsDetail, Error, SetupKitCreateRequest>({
    mutationFn: (request) => createKit(siteId, request),
    onSuccess: (data) => {
      queryClient.setQueryData(onboardingKeys.kits(siteId), data);
    },
  });
}

// Page size for the history panel — the backend default is 50, capped at 200.
export const APPLY_HISTORY_PAGE_SIZE = 20;

// The drawer's one-timeline feed: apply rows and save-progress snapshots in a
// single server-side cursor stream. NO SPACES in the comma list — the backend
// validates each member and 400s empty or unknown ones.
export const HISTORY_FEED_SECTION = "copy_applied,snapshot";

// Same self-hiding contract as useKits: `retry: false` and consumers render
// nothing without data, so against a backend without the snapshot feature the
// comma list 400s once and the panel never renders.
export function useBrandBookHistoryFeed(siteId: SiteId) {
  return useInfiniteQuery<SetupHistoryDetail, Error>({
    queryKey: onboardingKeys.history(siteId, HISTORY_FEED_SECTION),
    queryFn: ({ pageParam }) =>
      getSetupHistory(siteId, {
        section: HISTORY_FEED_SECTION,
        limit: APPLY_HISTORY_PAGE_SIZE,
        before: (pageParam as string) || undefined,
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.entries.length > 0
        ? lastPage.entries[lastPage.entries.length - 1].versionId
        : undefined,
    enabled: !!siteId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });
}

// As-of reconstruction backing the panel's Preview. Historical states are
// immutable, so cache them indefinitely.
export function useSetupAsOf(
  siteId: SiteId,
  at: string | null,
  kit: string = DEFAULT_KIT_ID,
) {
  return useQuery<SetupResponse, Error>({
    queryKey: onboardingKeys.asOf(siteId, at ?? "", kit),
    queryFn: () => getSetupAsOf(siteId, at ?? "", kit),
    enabled: !!siteId && !!at,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

// One apply run's per-tile copy artifact (history section frame_copy).
// 404 is an expected outcome (legacy-service applies, pre-artifact history,
// voice-unchanged skips) — consumers show a "no copy recorded" note.
export function useFrameCopyArtifact(siteId: SiteId, versionId: string | null) {
  return useQuery<SetupHistoryVersionDetail, Error>({
    queryKey: onboardingKeys.historyVersion(
      siteId,
      "frame_copy",
      versionId ?? "",
    ),
    queryFn: () => getHistoryVersion(siteId, "frame_copy", versionId ?? ""),
    enabled: !!siteId && !!versionId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

// Restore (complete revert to an instant) or per-tile revert, targeting one
// kit's brand book.
export type RevertSetupVariables = SetupRevertRequest & {
  kit?: string;
};

export function useRevertSetup(siteId: SiteId) {
  const queryClient = useQueryClient();
  return useMutation<SetupResponse, Error, RevertSetupVariables>({
    mutationFn: ({ kit, ...request }) => revertSetup(siteId, request, kit),
    onSuccess: (data, variables) => {
      const kit = variables.kit ?? DEFAULT_KIT_ID;
      clearDraft(siteId, kit);
      queryClient.setQueryData(onboardingKeys.allSteps(siteId, kit), data);
      for (const step of ["intro", "styling", "operation", "voice"] as const) {
        void queryClient.invalidateQueries({
          queryKey: onboardingKeys[step](siteId, kit),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: onboardingKeys.history(siteId),
        exact: false,
      });
    },
  });
}

export type CreateSnapshotVariables = {
  label: string;
  kit?: string;
};

export function useCreateSnapshot(siteId: SiteId) {
  const queryClient = useQueryClient();
  return useMutation<SetupHistoryEntryDetail, Error, CreateSnapshotVariables>({
    mutationFn: ({ label, kit }) => createSnapshot(siteId, { label }, kit),
    onSuccess: (entry) => {
      // Insert the returned entry at the head of the drawer's cached feed so
      // the new save point appears immediately, no refetch.
      queryClient.setQueryData<InfiniteData<SetupHistoryDetail>>(
        onboardingKeys.history(siteId, HISTORY_FEED_SECTION),
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page, index) =>
                  index === 0
                    ? { ...page, entries: [entry, ...page.entries] }
                    : page,
                ),
              }
            : old,
      );
    },
  });
}

export function useDeleteSnapshot(siteId: SiteId) {
  const queryClient = useQueryClient();
  return useMutation<SetupSnapshotDeleteDetail, Error, { versionId: string }>({
    mutationFn: ({ versionId }) => deleteSnapshot(siteId, versionId),
    onSuccess: (_data, { versionId }) => {
      // Drop the row from every cached page — the delete removed only the
      // label object, so nothing else in the feed changes.
      queryClient.setQueryData<InfiniteData<SetupHistoryDetail>>(
        onboardingKeys.history(siteId, HISTORY_FEED_SECTION),
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  entries: page.entries.filter(
                    (e) => e.versionId !== versionId,
                  ),
                })),
              }
            : old,
      );
    },
  });
}

export function useDeleteKit(siteId: SiteId) {
  const queryClient = useQueryClient();
  return useMutation<SetupKitsDetail, Error, { kitId: string }>({
    mutationFn: ({ kitId }) => deleteKit(siteId, kitId),
    onSuccess: (data, { kitId }) => {
      clearDraft(siteId, kitId);
      queryClient.setQueryData(onboardingKeys.kits(siteId), data);
    },
  });
}
